import { useCallback, useEffect, useRef, useContext } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import get from "lodash/get";
import {
	clearBooking as clearBookingAction,
	clearBookingQuotationAndPayment as clearBookingQuotationAndPaymentAction,
	updateBooking as updateBookingAction,
} from "app/pages/Booking/bookingActionCreators";
import SmartDPListing from "app/pages/SmartDP/Listing/SmartDPListing";
import { parse } from "qs";
import { useLocation, useNavigate } from "react-router-dom";

import PropTypes from "prop-types";
import {
	fetchSDPAvailableDepartureDate as fetchSDPAvailableDepartureDateAction,
	fetchSdpQuote as fetchSdpQuoteAction,
	fetchSdpQuoteWithCurrency as fetchSdpQuoteWithCurrencyAction,
	resetSDPSearchQueryString as resetSDPSearchQueryStringAction,
	saveSDPProduct as saveSDPProductAction,
	saveSDPSearchQueryString as saveSDPSearchQueryStringAction,
	setSdpQuotePayloadError as setSdpQuotePayloadErrorAction,
} from "app/pages/SmartDP/smartDPActionCreators";
import {
	buildSDPAvailabilityUri,
	buildSDPQuotePayload,
	buildStopoverQuotePayload,
	findQuotePayloadDuration,
	mapQueryParamsToBooking,
	transformAvailabilityDates,
} from "app/utils/smartDPUtils";
import {
	getBoardTypesDescriptions,
	getSDPProducts,
} from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterSelectors";
import { clearProduct as clearProductAction } from "app/pages/Listing/productActionCreators";
import {
	calculateSDPBasePrice,
	getSelectedStopoverCity,
	getUnavailableAccommodation,
} from "app/pages/SmartDP/smartDPSelectors";
import {
	DESTINATION_LEVELS,
	FLAGSHIP_SCREENVIEW_KPIS,
	HTTP_STATUS_CODES,
	QUOTATION_CODE_STATUS,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { mapProductToTag } from "app/utils/analytics-store-mapper";
import { clearQuotation as clearQuotationAction } from "app/pages/Booking/Quotation/quotationActionCreators";
import { FSSdkStatus, HitType, useFlagship, useFsFlag } from "@flagship.io/react-sdk";
import { isServerSide } from "app/utils/utils";
import {
	fetchStopoverFlightOptions as fetchStopoverFlightOptionsAction,
	fetchStopoverQuote as fetchStopoverQuoteAction,
} from "app/pages/Stopover/stopoverActionCreators";
import {
	getSelectedStopoverDuration,
	getLuggageTotalPrice,
	getFlightUpgrade,
	getTravellersTotalCount,
	getDestinationResort,
} from "app/pages/Booking/bookingSelectors";
import { useDatadog } from "app/utils/datadog/useDatadog";
import { updateExchangeRate as updateExchangeRateAction } from "app/reducers/exchangeRateActionCreators";
import useSearchHistory from "app/features/SearchEngine/LastSearch/hooks/useSearchHistory";
import { intervalToDuration } from "date-fns";
import { getHideDiscountHBX } from "app/reducers/brandSelector";
import appGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";

const QUOTE_EXPIRE_HOURS = 2;

const getPricePerTraveller = (price, count) =>
	Number.isFinite(price) && Number.isFinite(count) && count > 0 ? Math.ceil(price / count) : NaN;

const getBoardCodesFromAccommodation = accommodation => {
	const items = Array.isArray(accommodation?.accommodationItems)
		? accommodation.accommodationItems
		: [];

	const codes = items.flatMap(item => item.boards ?? []);

	return new Set(codes.map(code => code?.boardTypeCode).filter(Boolean));
};

const getBoardLabel = (boardCodes, code) => {
	return boardCodes.find(board => board.boardTypeCode === code)?.label ?? null;
};

const findCheapestAccommodationUpgradeByBoardCode = (accommodationItems, boardCode) => {
	if (!Array.isArray(accommodationItems) || !boardCode) {
		return { cheapestAccommodationItem: null, upgradePrice: null };
	}

	let cheapestItem = null;
	let cheapestPrice = Infinity;

	accommodationItems.forEach(item => {
		const itemUpgrade = Number(item.upgradePrice) || 0;

		(item.boards ?? []).forEach(board => {
			if (board.boardTypeCode !== boardCode) {
				return;
			}

			const codeUpgrade = Number(board.upgradePrice) || 0;
			const totalUpgrade = itemUpgrade + codeUpgrade;

			if (totalUpgrade < cheapestPrice) {
				cheapestPrice = totalUpgrade;
				cheapestItem = item;
			}
		});
	});

	if (!cheapestItem) {
		return { cheapestAccommodationItem: null, upgradePrice: null };
	}

	return { cheapestAccommodationItem: cheapestItem, upgradePrice: cheapestPrice };
};

const buildAvailableBoards = (accommodation, boardCodes, basePrice, travellersCount) => {
	const codes = getBoardCodesFromAccommodation(accommodation);
	const availableBoards = [];

	codes.forEach(code => {
		const label = getBoardLabel(boardCodes, code);

		if (!label) {
			return;
		}

		const { upgradePrice, cheapestAccommodationItem } =
			findCheapestAccommodationUpgradeByBoardCode(accommodation?.accommodationItems, code);

		if (upgradePrice === null || cheapestAccommodationItem === null) {
			return;
		}

		const totalPrice = basePrice + upgradePrice;
		const pricePerTraveller = getPricePerTraveller(totalPrice, travellersCount);
		const cheapestAccommodationItemCode =
			cheapestAccommodationItem?.items?.[0]?.accommodationItemCode;
		const accommodationLabel = accommodation?.accommodationItemDescriptions?.find(
			description => description?.accommodationItemCode === cheapestAccommodationItemCode
		)?.label;

		availableBoards.push({
			boardCode: code,
			boardLabel: label,
			totalPrice,
			pricePerTraveller,
			accommodationLabel,
		});
	});

	return availableBoards;
};

const formatAccommodations = ({ accommodations, boardCodes, basePrice, travellersCount }) =>
	(accommodations ?? []).map(accommodation => ({
		...accommodation,
		availableBoards: buildAvailableBoards(
			accommodation,
			boardCodes,
			basePrice,
			travellersCount
		),
	}));

const SmartDPListingContainer = props => {
	const { track } = useAnalytics();
	const navigate = useNavigate();

	const stateProps = useSelector(mapStateToProps, shallowEqual);
	const {
		partnerCode,
		destinations = [],
		departureCities = [],
		smartDPCode = "",
		cabins = [],
		currency = "",
		shop,
		searchQueryString,
		sdpQuoteTimestamp,
		accommodationsList = [],
		boardCodes = [],
		quotationItems = [],
		luggageUpgradePrice = 0,
		flightUpgradePrice = 0,
		travellersCount = 1,
	} = stateProps;

	const dispatch = useDispatch();
	const fetchStopoverFlightOptions = useCallback(
		(...args) => dispatch(fetchStopoverFlightOptionsAction(...args)),
		[dispatch]
	);
	const setSdpQuotePayloadError = useCallback(
		(...args) => dispatch(setSdpQuotePayloadErrorAction(...args)),
		[dispatch]
	);
	const fetchSdpQuote = useCallback(
		(...args) => dispatch(fetchSdpQuoteAction(...args)),
		[dispatch]
	);
	const fetchStopoverQuote = useCallback(
		(...args) => dispatch(fetchStopoverQuoteAction(...args)),
		[dispatch]
	);
	const saveSDPSearchQueryString = useCallback(
		(...args) => dispatch(saveSDPSearchQueryStringAction(...args)),
		[dispatch]
	);
	const fetchSdpQuoteWithCurrency = useCallback(
		(...args) => dispatch(fetchSdpQuoteWithCurrencyAction(...args)),
		[dispatch]
	);
	const resetSDPSearchQueryString = useCallback(
		(...args) => dispatch(resetSDPSearchQueryStringAction(...args)),
		[dispatch]
	);
	const fetchSDPAvailableDepartureDate = useCallback(
		(...args) => dispatch(fetchSDPAvailableDepartureDateAction(...args)),
		[dispatch]
	);
	const updateBooking = useCallback(
		(...args) => dispatch(updateBookingAction(...args)),
		[dispatch]
	);
	const clearProduct = useCallback(
		(...args) => dispatch(clearProductAction(...args)),
		[dispatch]
	);
	const clearBookingQuotationAndPayment = useCallback(
		(...args) => dispatch(clearBookingQuotationAndPaymentAction(...args)),
		[dispatch]
	);
	const updateExchangeRate = useCallback(
		(...args) => dispatch(updateExchangeRateAction(...args)),
		[dispatch]
	);
	const saveSDPProduct = useCallback(
		(...args) => dispatch(saveSDPProductAction(...args)),
		[dispatch]
	);
	const clearBooking = useCallback(
		(...args) => dispatch(clearBookingAction(...args)),
		[dispatch]
	);
	const clearQuotation = useCallback(
		(...args) => dispatch(clearQuotationAction(...args)),
		[dispatch]
	);

	const dispatchProps = {
		fetchStopoverQuote,
		fetchSdpQuote,
		fetchSdpQuoteWithCurrency,
		fetchSDPAvailableDepartureDate,
		updateBooking,
		saveSDPProduct,
		saveSDPSearchQueryString,
		resetSDPSearchQueryString,
		fetchStopoverFlightOptions,
		clearProduct,
		clearBooking,
		clearBookingQuotationAndPayment,
		clearQuotation,
		setSdpQuotePayloadError,
		updateExchangeRate,
	};

	const basePrice = (quotationItems || []).reduce(
		(acc, item) => acc + (Number(item?.price) || 0),
		0
	);

	const price = Number(basePrice + luggageUpgradePrice + flightUpgradePrice);

	const formattedAccommodations = formatAccommodations({
		accommodations: accommodationsList,
		boardCodes,
		basePrice: price,
		travellersCount,
	});

	const { search, pathname } = useLocation();
	const queryParams = parse(search, { ignoreQueryPrefix: true });

	const {
		departureCityCode,
		destinationIATA,
		destinationResortCode,
		departureDate,
		duration,
		cabinCode,
		stopoverCityId,
		stopoverDirection,
		endDate,
		occupancies,
	} = queryParams;

	const previousCurrencyRef = useRef(currency);
	const isCurrencyChanged = previousCurrencyRef.current !== currency;

	const isStopover = pathname.includes("/stopover");
	const isSdp = pathname.includes("/sdp");
	const isMapView = pathname.includes("/map");

	const sdpFunnelFlags = useFsFlag("sdp_funnel_flags");
	const sdpFunnelFlagsValues = sdpFunnelFlags.getValue(undefined);

	const { resolution } = useContext(appGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const { sendHits, sdkStatus } = useFlagship();

	const isFlagshipSDKReady = sdkStatus === FSSdkStatus?.SDK_INITIALIZED;

	const { addSearch } = useSearchHistory(
		isStopover ? WEBSITE_TUNNEL_TYPES.STOPOVER : WEBSITE_TUNNEL_TYPES.HOLIDAYS
	);

	const { addAction } = useDatadog();

	useEffect(() => {
		const fetchQuoteWithCurrency = async requestBody => {
			const response = await fetchSdpQuoteWithCurrency(requestBody);
			updateExchangeRate({
				usd: response?.data?.exchangeRateUSD,
			});
			return response;
		};
		if (currency && isCurrencyChanged && !isStopover) {
			previousCurrencyRef.current = currency;
			const requestBody = {
				currency: currency,
				bookingFlowId: smartDPCode,
			};
			fetchQuoteWithCurrency(requestBody);
		}
	}, [isCurrencyChanged, currency, smartDPCode, isStopover]);

	useEffect(() => {
		clearProduct();

		// dans la parcours stopover, on choisit le vol et les bagages à l'étape de la listing
		// donc on ne veut pas reset le vol et les bagages choisit par le user.
		if (!isStopover) {
			clearBookingQuotationAndPayment();
		}

		sendHits([
			{
				type: HitType.PAGE_VIEW,
				documentLocation: window.location.origin + window.location.pathname,
			},
			{
				type: HitType.SCREEN_VIEW,
				documentLocation: FLAGSHIP_SCREENVIEW_KPIS.SCREENVIEW_SDP_LISTING,
			},
		]);
	}, [isStopover]);

	const fetchStopoverProducts = (queryParams, bookingToSave, searchQueryString) => {
		const sdpQuotePayload = buildStopoverQuotePayload({
			queryParams,
			currency,
			marketingCode: partnerCode,
			sdpFunnelFlags: sdpFunnelFlagsValues,
			destinations,
		});

		const queryParamsSdpFetchProducts = {
			...queryParams,
			destinationResortCode: bookingToSave?.destinationResort?.code || "",
			destinationResortLabel: bookingToSave?.destinationResort?.label || "",
			departureCityLabel: bookingToSave?.departureCity?.label || "",
		};

		fetchStopoverQuote(sdpQuotePayload).then(response => {
			const httpStatusCode = response.status;
			const data = response.data || {};
			if (
				httpStatusCode === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
				httpStatusCode === HTTP_STATUS_CODES.GATEWAY_TIMEOUT
			) {
				if (
					![
						QUOTATION_CODE_STATUS.FULL_FLIGHT,
						QUOTATION_CODE_STATUS.FULL_PROPERTY,
						QUOTATION_CODE_STATUS.FULL_TRANSFER,
					].includes(data.status)
				) {
					navigate(
						{
							pathname: "/technical-error",
						},
						{
							replace: true,
							state: {
								errorMessage: data.message,
							},
						}
					);
				}
				track("stopover_fetch_products", {
					httpStatusCode,
					queryParams: queryParamsSdpFetchProducts,
				});
			} else if (httpStatusCode === HTTP_STATUS_CODES.BAD_REQUEST) {
				const status = data.status;

				track("stopover_fetch_products", {
					httpStatusCode,
					status,
					queryParams: queryParamsSdpFetchProducts,
				});
			} else if (httpStatusCode === HTTP_STATUS_CODES.FORBIDDEN) {
				// reset the stored query string so after authentication the quote can be relaunch
				resetSDPSearchQueryString();

				const onauthsuccess = encodeURIComponent(
					`${pathname.replace(`/${shop}`, "")}${search}`
				);

				navigate(
					{
						pathname: "/booking-auth",
						search: `?onauthsuccess=${onauthsuccess}`,
					},
					{ replace: true }
				);
			} else {
				const status = data.status;
				const productCount = data.packages?.accommodationsList.length;
				const specialOffer = get(data, "specialOffer");
				const products = data.packages?.accommodationsList || [];
				const stopDuration = data?.packages?.accommodationNights;

				if (productCount > 0) {
					addSearch(bookingToSave, searchQueryString);
				}

				updateExchangeRate({
					usd: data?.exchangeRateUSD,
				});

				const mappedProducts = products.map(mapProductToTag).map((product, position) => ({
					...product,
					position,
				}));

				track("product_list", {
					sortedBy: data?.defaultSort,
					list: "PRODUCT_LIST",
					productCount,
					queryParams: queryParamsSdpFetchProducts,
					products: mappedProducts.slice(0, 12),
				});

				track("stopover_fetch_products", {
					httpStatusCode,
					status,
					productCount,
					queryParams: queryParamsSdpFetchProducts,
					specialOffer,
				});

				const includedFlight = data.packages?.flights.find(flight => flight.included);

				if (includedFlight?.code) {
					fetchStopoverFlightOptions({
						flight: includedFlight?.code,
						bookingFlowId: response?.data.bookingFlowId,
						stopOverDuration: Number(stopDuration),
					}).then(response => {
						const flightOptionsHttpStatus = response.status;

						if (
							!flightOptionsHttpStatus ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.GATEWAY_TIMEOUT ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.BAD_REQUEST ||
							flightOptionsHttpStatus === HTTP_STATUS_CODES.FORBIDDEN
						) {
							track("flightOptions_fail");
						}
					});
				}
			}
		});
	};

	const displayNoResultMessage = queryParams => {
		saveSDPSearchQueryString(search);
		addAction("sdp_quote_not_called", {
			departureCityCode: queryParams.departureCityCode,
			destinationResortCode: queryParams.destinationResortCode,
			destinationIATA: queryParams.destinationIATA,
		});
		setSdpQuotePayloadError();
	};

	const handleSmartDPQuoteResponse = (
		response,
		queryParams,
		bookingToSave,
		searchQueryString
	) => {
		const httpStatusCode = response?.status;
		const data = response?.data || {};
		const status = data?.status;

		if (
			httpStatusCode === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
			httpStatusCode === HTTP_STATUS_CODES.GATEWAY_TIMEOUT
		) {
			navigate(
				{
					pathname: "/technical-error",
				},
				{
					replace: true,
					state: {
						errorMessage: data.message,
					},
				}
			);
			track("sdp_fetch_products", {
				httpStatusCode,
				queryParams,
			});
		} else if (httpStatusCode === HTTP_STATUS_CODES.BAD_REQUEST) {
			track("sdp_fetch_products", {
				httpStatusCode,
				status,
				queryParams,
			});
		} else if (httpStatusCode === HTTP_STATUS_CODES.FORBIDDEN) {
			track("sdp_fetch_products", {
				httpStatusCode,
				status,
				queryParams,
			});

			// reset the stored query string so after authentication the quote peut être relancé
			resetSDPSearchQueryString();

			const onauthsuccess = encodeURIComponent(
				`${pathname.replace(`/${shop}`, "")}${search}`
			);

			navigate(
				{
					pathname: "/booking-auth",
					search: `?onauthsuccess=${onauthsuccess}`,
				},
				{ replace: true }
			);
		} else {
			const accommodationId = queryParams?.accommodationId;
			const isPartialSuccess = data?.status === QUOTATION_CODE_STATUS.PARTIAL_SUCCESS;

			let matchedAccommodation = null;
			if (accommodationId) {
				if (
					isPartialSuccess &&
					String(data.unavailableAccommodation?.id) === String(accommodationId)
				) {
					matchedAccommodation = data.unavailableAccommodation;
				} else {
					matchedAccommodation = data.accommodationsList?.find(
						accommodation => String(accommodation.id) === String(accommodationId)
					);
				}
			}

			const productCount = data?.accommodationsList?.length || 0;

			if (productCount > 0) {
				const searchEntry = matchedAccommodation
					? {
							...bookingToSave,
							searchedAccommodation: {
								id: String(accommodationId),
								name: matchedAccommodation.name,
								places: [
									{
										type: DESTINATION_LEVELS.RESORT,
										id: Number(bookingToSave.destinationResort?.code),
									},
								],
							},
						}
					: bookingToSave;

				addSearch(searchEntry, searchQueryString);
			}

			const discountedProductsCount =
				data.accommodationsList?.filter(accommodation => {
					return accommodation?.accommodationItems?.some(item =>
						item?.boards?.some(board => board?.discount > 0)
					);
				}).length || 0;

			const specialOffer = get(data, "specialOffer");

			const products = get(data, "accommodationsList", []);

			updateExchangeRate({
				usd: data?.exchangeRateUSD,
			});

			const mappedProducts = products.map(mapProductToTag).map((product, position) => ({
				...product,
				position,
			}));

			track("product_list", {
				sortedBy: data?.defaultSort,
				list: "PRODUCT_LIST",
				productCount,
				queryParams,
				products: mappedProducts.slice(0, 12),
			});

			track("sdp_fetch_products", {
				httpStatusCode,
				status,
				productCount,
				queryParams,
				specialOffer,
				discountedProductsCount,
			});

			// sur mobile et map view, si on a un message d'indisponibilite de l'hotel ...
			if (isMobile && isMapView && status === QUOTATION_CODE_STATUS.PARTIAL_SUCCESS) {
				// .. alors on affiche le message d'erreur sur le vue liste
				navigate(
					{
						pathname: ".",
						search,
					},
					{
						replace: true,
					}
				);
			}
		}
	};

	const fetchSmartDPProducts = (queryParams, bookingToSave, searchQueryString) => {
		const durationFromQuery = Number(queryParams?.duration);

		const queryParamsSdpFetchProducts = {
			...queryParams,
			destinationResortCode: bookingToSave?.destinationResort?.code || "",
			destinationResortLabel: bookingToSave?.destinationResort?.label || "",
			departureCityLabel: bookingToSave?.departureCity?.label || "",
		};

		const code =
			queryParams?.fromHotelPage && smartDPCode?.length > 0 ? smartDPCode : undefined;

		if (durationFromQuery > 0) {
			const sdpQuotePayload = buildSDPQuotePayload({
				queryParams,
				currency,
				partnerCode,
				sdpFunnelFlags: sdpFunnelFlagsValues,
				destinations,
				duration: durationFromQuery,
				code,
			});

			fetchSdpQuote(sdpQuotePayload).then(response => {
				handleSmartDPQuoteResponse(
					response,
					queryParamsSdpFetchProducts,
					bookingToSave,
					searchQueryString
				);
			});
		} else {
			const sdpAvailabilityUri = buildSDPAvailabilityUri({
				destinations,
				departureCities,
				queryParams,
			});

			if (!sdpAvailabilityUri) {
				displayNoResultMessage(queryParams);
			} else {
				fetchSDPAvailableDepartureDate(sdpAvailabilityUri).then(response => {
					if (response.status === HTTP_STATUS_CODES.OK) {
						const availabilitiesDate = transformAvailabilityDates(response.data);

						const quotePayloadDuration = findQuotePayloadDuration({
							availabilitiesDate,
							queryParams,
						});

						updateBooking({
							duration: {
								value: quotePayloadDuration,
							},
						});

						if (!quotePayloadDuration) {
							displayNoResultMessage(queryParams);
						} else {
							const sdpQuotePayload = buildSDPQuotePayload({
								queryParams,
								currency,
								partnerCode,
								sdpFunnelFlags: sdpFunnelFlagsValues,
								destinations,
								duration: quotePayloadDuration,
								code,
							});

							fetchSdpQuote(sdpQuotePayload).then(response => {
								handleSmartDPQuoteResponse(
									response,
									queryParamsSdpFetchProducts,
									bookingToSave,
									searchQueryString
								);
							});
						}
					} else {
						displayNoResultMessage(queryParams);
					}
				});
			}
		}
	};

	useEffect(() => {
		const today = new Date();
		let duration = {};

		if (sdpQuoteTimestamp) {
			duration = intervalToDuration({
				start: today,
				end: new Date(sdpQuoteTimestamp),
			});
		}

		// updateBooking should be done here after checking if search has changed
		const bookingToSave = mapQueryParamsToBooking({
			destinations,
			departureCities,
			cabins,
			queryParams,
		});

		updateBooking(bookingToSave);

		const hourCount = Math.abs(duration.hours || 0);

		if (
			(searchQueryString !== search || hourCount > QUOTE_EXPIRE_HOURS) &&
			destinations.length > 0 &&
			(isFlagshipSDKReady || (!isServerSide && window.Cypress))
		) {
			if (isStopover) {
				fetchStopoverProducts(queryParams, bookingToSave, search);
			} else {
				fetchSmartDPProducts(queryParams, bookingToSave, search);
			}

			saveSDPSearchQueryString(search);
		}
	}, [
		isFlagshipSDKReady,
		currency,
		destinations,
		departureCityCode, // do not delete, needed to refetch when changing from search engine
		destinationIATA, // do not delete, needed to refetch when changing from search engine
		destinationResortCode, // do not delete, needed to refetch when changing from search engine
		departureDate, // do not delete, needed to refetch when changing from search engine
		duration, // do not delete, needed to refetch when changing from search engine
		cabinCode, // do not delete, needed to refetch when changing  from search engine
		stopoverCityId, // do not delete, needed to refetch when changing departure city from search engine
		stopoverDirection, // do not delete, needed to refetch when changing departure city from search engine
		endDate, // do not delete, needed to refetch when changing departure city from search engine
		JSON.stringify(occupancies), // do not delete, needed to refetch when changing departure city from search engine
	]);

	return (
		<SmartDPListing
			{...props}
			{...stateProps}
			{...dispatchProps}
			accommodationsList={formattedAccommodations}
			isStopOver={isStopover}
			isSdp={isSdp}
		/>
	);
};

const mapStateToProps = state => {
	const selectedStopoverCity = getSelectedStopoverCity(state);
	return {
		shop: state.shop,
		partnerCode: state.partner.code,
		fetchSDPProductsStatus: state.smartDP.status,
		smartDPCode: state.smartDP?.code,
		currency: state?.currency,
		sortBy: state.smartDP?.sortedBy,
		hasSdpSpecialOffer: state.smartDP.specialOffer, // true si le back renvoi au moins un vol remboursable
		specialOfferInfos: state.smartDP.specialOfferInfos,
		accommodationsList: getSDPProducts(state),
		unavailableAccommodation: getUnavailableAccommodation(state),
		basePrice: calculateSDPBasePrice(state),
		destinations: state.smartDP.destinations,
		departureCities: state.smartDP.departureCities,
		cabins: state.smartDP.cabins,
		selectedStopoverCityName: selectedStopoverCity?.label,
		selectedStopoverDuration: getSelectedStopoverDuration(state),
		quotationItems: state.smartDP.quotationItems,
		availabilitiesDate: state.smartDP.availabilitiesDate,
		luggageUpgradePrice: getLuggageTotalPrice(state),
		flightUpgradePrice: getFlightUpgrade(state)?.upgradePrice,
		searchQueryString: state.smartDP.searchQueryString,
		sdpQuoteTimestamp: state.smartDP.timestamp,
		currencyCode: state?.currency,
		destinationType: getDestinationResort(state)?.type,
		currentDestinationLabel: getDestinationResort(state)?.label,
		travellersCount: getTravellersTotalCount(state),
		boardCodes: getBoardTypesDescriptions(state),
		hideDiscountHBX: getHideDiscountHBX(state),
	};
};

SmartDPListingContainer.propTypes = {
	shop: PropTypes.string,
	sdpQuoteTimestamp: PropTypes.number,
	fetchSdpQuote: PropTypes.func,
	fetchStopoverFlightOptions: PropTypes.func,
	fetchStopoverQuote: PropTypes.func,
	fetchSdpQuoteWithCurrency: PropTypes.func,
	fetchSDPAvailableDepartureDate: PropTypes.func,
	smartDPCode: PropTypes.string,
	saveSDPSearchQueryString: PropTypes.func,
	setSdpQuotePayloadError: PropTypes.func,
	resetSDPSearchQueryString: PropTypes.func,
	partnerCode: PropTypes.string,
	searchQueryString: PropTypes.string,
	updateBooking: PropTypes.func,
	clearProduct: PropTypes.func,
	currency: PropTypes.string,
	clearBookingQuotationAndPayment: PropTypes.func,
	booking: PropTypes.object,
	destinations: PropTypes.array,
	sortBy: PropTypes.string,
	departureCities: PropTypes.array,
	cabins: PropTypes.array,
	updateExchangeRate: PropTypes.func,
	accommodationsList: PropTypes.array,
	boardCodes: PropTypes.array,
	quotationItems: PropTypes.array,
	luggageUpgradePrice: PropTypes.number,
	flightUpgradePrice: PropTypes.number,
	travellersCount: PropTypes.number,
};

export default SmartDPListingContainer;
